import React, { useCallback, useEffect, useRef, useState, forwardRef } from 'react';
import {
  Content,
  Header,
  Progress,
  Table,
  TableColumn,
} from '@backstage/core-components';
import {
  makeStyles,
  Typography,
  useTheme,
  IconButton,
  Tooltip,
  CircularProgress
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import RefreshIcon from '@material-ui/icons/Sync';
import RetryIcon from '@material-ui/icons/Refresh';
import axios from 'axios';
import { QueueConsumersPage } from './QueueConsumersPage';
import { configApiRef, identityApiRef, useApi } from '@backstage/core-plugin-api';
import { BackstageTheme } from '@backstage/theme';
import { useServiceRegistryTranslation } from '../../i18n/ServiceRegistryLanguageProvider';
import { tablePaginationLocalization } from '../../i18n/pagination';

interface QueueData {
  queueName: string;
  bindSuccessCount: number;
  accessType: string;
  bindRequestCount: number;
  txFlowsUri: string;
  currentConsumers?: number;
  currentMessageCount?: number;
  isLoading?: boolean;
  error?: string;
}

interface ExtendedDetails {
  queueName: string;
  currentConsumers: number;
  currentMessageCount: number;
  error?: string;
  lastUpdated?: number;
}

interface PaginatedQueueResponse {
  data: QueueData[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
  nextCursor?: string;
}

interface BulkExtendedDetailsResponse {
  results: ExtendedDetails[];
}

const useStyles = makeStyles(
  (theme: BackstageTheme) => ({
    root: {
      "& $actions": {
        paddingRight: theme.spacing(1),
      },
      "& table $actions": {
        paddingRight: theme.spacing(0),
      },
    },
    actions: {
      display: "flex",
      justifyContent: "right"
    },
    noDataMessage: {
      padding: theme.spacing(2),
      display: "flex",
      justifyContent: "center",
    },
    errorText: {
      color: theme.palette.error.main,
      fontSize: '0.75rem',
    },
    linkStyle: {
      textDecoration: 'none',
      color: theme.palette.primary.main,
      cursor: 'pointer',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  }),
  { name: 'QueueListPage' }
);

// Utility function to retry API calls with exponential backoff
const retryWithBackoff = async (
  fn: () => Promise<any>,
  maxRetries: number = 3,
  initialDelay: number = 500,
  backoffMultiplier: number = 2
) => {
  let retries = 0;
  let delay = initialDelay;

  while (retries < maxRetries) {
    try {
      return await fn();
    } catch (error) {
      retries++;
      if (retries >= maxRetries) {
        throw error;
      }
      await new Promise(resolve => setTimeout(resolve, delay));
      delay *= backoffMultiplier;
    }
  }
};

const QueueListPage = () => {
  const { t, formatNumber, formatUtcDateTime } = useServiceRegistryTranslation();
  const classes = useStyles();
  const theme = useTheme<BackstageTheme>();
  const tableRef = useRef<any>();

  const [queues, setQueues] = useState<QueueData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedQueue, setSelectedQueue] = useState<QueueData | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string>('');

  // Pagination state
  const [totalCount, setTotalCount] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(20);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [localSearchQuery, setLocalSearchQuery] = useState<string>('');
  const [currentCursor, setCurrentCursor] = useState<string | undefined>();
  const [cursorHistory, setCursorHistory] = useState<(string | undefined)[]>([undefined]);

  // Extended details caching
  const [extendedDetailsCache, setExtendedDetailsCache] = useState<Map<string, ExtendedDetails>>(new Map());
  const [fetchingExtendedDetails, setFetchingExtendedDetails] = useState<Set<string>>(new Set());
  const [retryingQueues, setRetryingQueues] = useState<Set<string>>(new Set());

  const configApi = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const backendUrl = configApi.getString('backend.baseUrl');

  // Cache expiry time (5 minutes)
  const CACHE_EXPIRY_MS = 5 * 60 * 1000;

  const updateLastUpdatedTime = () => {
    setLastUpdated(formatUtcDateTime(new Date()));
  };

  // Enhanced debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localSearchQuery !== searchQuery) {
        console.log('Search query changed from:', searchQuery, 'to:', localSearchQuery);
        setSearchQuery(localSearchQuery);
        setPage(0);
        setCursorHistory([undefined]);
        setCurrentCursor(undefined);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [localSearchQuery, searchQuery]);

  // Check if extended details are cached and not expired
  const isExtendedDetailsCached = (queueName: string): boolean => {
    const cached = extendedDetailsCache.get(queueName);
    if (!cached || !cached.lastUpdated) return false;
    
    const now = Date.now();
    return (now - cached.lastUpdated) < CACHE_EXPIRY_MS;
  };

  // Get cached extended details
  const getCachedExtendedDetails = (queueName: string): ExtendedDetails | null => {
    if (isExtendedDetailsCached(queueName)) {
      return extendedDetailsCache.get(queueName) || null;
    }
    return null;
  };

  // Update cache with new extended details
  const updateExtendedDetailsCache = (details: ExtendedDetails[]) => {
    setExtendedDetailsCache(prevCache => {
      const newCache = new Map(prevCache);
      details.forEach(detail => {
        newCache.set(detail.queueName, {
          ...detail,
          lastUpdated: Date.now()
        });
      });
      return newCache;
    });
  };

  // Fetch extended details for multiple queues using bulk API
  const fetchBulkExtendedDetails = async (queuesToFetch: QueueData[], token: string) => {
    if (queuesToFetch.length === 0) return;

    console.log(`Fetching bulk extended details for ${queuesToFetch.length} queues`);

    // Mark queues as being fetched
    const queueNames = queuesToFetch.map(q => q.queueName);
    setFetchingExtendedDetails(prev => {
      const newSet = new Set(prev);
      queueNames.forEach(name => newSet.add(name));
      return newSet;
    });

    // Set loading state for queues
    setQueues(prevQueues =>
      prevQueues.map(q =>
        queueNames.includes(q.queueName)
          ? { ...q, isLoading: true, error: undefined }
          : q
      )
    );

    try {
      const bulkRequest = {
        queues: queuesToFetch.map(queue => ({
          queueName: queue.queueName,
          queueUrl: queue.txFlowsUri.replace('/txFlows', ''),
          txFlowsUrl: queue.txFlowsUri
        }))
      };

      const response = await retryWithBackoff(() =>
        axios.post<BulkExtendedDetailsResponse>(
          `${backendUrl}/api/queueBulkExtendedDetails`,
          bulkRequest,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            timeout: 45000, // Increased timeout for bulk requests
          }
        )
      );

      const results = response.data.results || [];
      
      // Update cache
      updateExtendedDetailsCache(results);

      // Update queues state with fetched details
      setQueues(prevQueues =>
        prevQueues.map(q => {
          const result = results.find(r => r.queueName === q.queueName);
          if (result) {
            return {
              ...q,
              currentConsumers: result.currentConsumers,
              currentMessageCount: result.currentMessageCount,
              isLoading: false,
              error: result.error
            };
          }
          return q;
        })
      );

      console.log(`Successfully fetched bulk extended details for ${results.length} queues`);
    } catch (error) {
      console.error('Error fetching bulk extended details:', error);
      
      // Update queues with error state
      setQueues(prevQueues =>
        prevQueues.map(q =>
          queueNames.includes(q.queueName)
            ? {
                ...q,
                isLoading: false,
                error: error instanceof Error ? error.message : 'Failed to load details'
              }
            : q
        )
      );
    } finally {
      // Remove from fetching set
      setFetchingExtendedDetails(prev => {
        const newSet = new Set(prev);
        queueNames.forEach(name => newSet.delete(name));
        return newSet;
      });
    }
  };

  // Fetch single queue extended details (for retry)
  const fetchSingleQueueExtendedDetails = async (queue: QueueData, token: string) => {
    try {
      setQueues(prevQueues =>
        prevQueues.map(q =>
          q.queueName === queue.queueName
            ? { ...q, isLoading: true, error: undefined }
            : q
        )
      );

      const queueUrl = queue.txFlowsUri.replace('/txFlows', '');
      const txFlowsUrl = queue.txFlowsUri;
      
      const response = await retryWithBackoff(() =>
        axios.post(
          `${backendUrl}/api/queueExtendedDetails`,
          { queueUrl, txFlowsUrl },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            timeout: 30000,
          }
        )
      );

      const details: ExtendedDetails = {
        queueName: queue.queueName,
        currentConsumers: response.data?.currentConsumers || 0,
        currentMessageCount: response.data?.currentMessageCount || 0,
        lastUpdated: Date.now()
      };

      // Update cache
      updateExtendedDetailsCache([details]);

      // Update queue state
      setQueues(prevQueues =>
        prevQueues.map(q =>
          q.queueName === queue.queueName
            ? {
                ...q,
                currentConsumers: details.currentConsumers,
                currentMessageCount: details.currentMessageCount,
                isLoading: false,
                error: undefined
              }
            : q
        )
      );
    } catch (error) {
      console.error(`Error fetching details for queue ${queue.queueName}:`, error);
      setQueues(prevQueues =>
        prevQueues.map(q =>
          q.queueName === queue.queueName
            ? {
                ...q,
                isLoading: false,
                error: error instanceof Error ? error.message : 'Failed to load details'
              }
            : q
        )
      );
    }
  };

  const fetchQueues = useCallback(async (forceRefresh: boolean = false) => {
    setLoading(true);
    try {
      const { token } = await identityApi.getCredentials();
      
      // For search, don't use cursor pagination
      const cursor = searchQuery.trim() ? undefined : (page < cursorHistory.length ? cursorHistory[page] : currentCursor);
      
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        pageSize: rowsPerPage.toString(),
      });

      if (searchQuery.trim()) {
        params.append('search', searchQuery.trim());
        console.log('Searching for:', searchQuery.trim());
      }

      if (cursor && !searchQuery.trim()) {
        params.append('cursor', cursor);
      }

      const url = `${backendUrl}/api/queueList?${params.toString()}`;
      console.log('Request URL:', url);

      const response = await retryWithBackoff(() =>
        axios.get(url, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          timeout: 30000,
        })
      );

      const paginatedData: PaginatedQueueResponse = response.data;
      console.log('Response data:', paginatedData);

      const safeData = {
        ...paginatedData,
        totalCount: paginatedData.totalCount || 0,
        data: paginatedData.data || []
      };

      // Initialize queues with cached extended details if available
      const queuesWithCachedDetails = safeData.data.map(queue => {
        const cached = getCachedExtendedDetails(queue.queueName);
        if (cached && !forceRefresh) {
          return {
            ...queue,
            currentConsumers: cached.currentConsumers,
            currentMessageCount: cached.currentMessageCount,
            isLoading: false,
            error: cached.error
          };
        } else {
          return {
            ...queue,
            currentConsumers: undefined,
            currentMessageCount: undefined,
            isLoading: false // Will be set to true when fetching
          };
        }
      });

      setQueues(queuesWithCachedDetails);
      setTotalCount(safeData.totalCount);

      // Update cursor history only if not searching
      if (!searchQuery.trim() && safeData.nextCursor && page + 1 >= cursorHistory.length) {
        setCursorHistory(prev => [...prev.slice(0, page + 1), safeData.nextCursor]);
      }

      if (!searchQuery.trim()) {
        setCurrentCursor(safeData.nextCursor);
      }

      updateLastUpdatedTime();

      // Identify queues that need extended details fetching
      const queuesToFetch = queuesWithCachedDetails.filter(queue => {
        const cached = getCachedExtendedDetails(queue.queueName);
        return !cached || forceRefresh;
      });

      // Fetch extended details for queues that don't have cached data
      if (queuesToFetch.length > 0) {
        console.log(`Need to fetch extended details for ${queuesToFetch.length} queues`);
        await fetchBulkExtendedDetails(queuesToFetch, token);
      } else {
        console.log('All extended details available from cache');
      }

    } catch (error) {
      console.error('Error fetching queue data:', error);
      setQueues([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, page, rowsPerPage, backendUrl, identityApi, currentCursor, cursorHistory, extendedDetailsCache]);

  useEffect(() => {
    fetchQueues();
  }, [fetchQueues]);

  const handlePageChange = (newPage: number) => {
    console.log('Page changed to:', newPage);
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    console.log('Rows per page changed to:', newRowsPerPage);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleSearchChange = (query: string) => {
    console.log('Search input changed to:', query);
    setLocalSearchQuery(query);
    if (query.trim() === '') {
      setSearchQuery('');
      setPage(0);
    }
  };

  const retryLoadingQueue = async (queue: QueueData) => {
    setRetryingQueues(prev => new Set(prev).add(queue.queueName));
    try {
      const { token } = await identityApi.getCredentials();
      await fetchSingleQueueExtendedDetails(queue, token);
    } finally {
      setRetryingQueues(prev => {
        const newSet = new Set(prev);
        newSet.delete(queue.queueName);
        return newSet;
      });
    }
  };

  const handleRefresh = () => {
    console.log('Refresh clicked - clearing cache and refetching');
    setExtendedDetailsCache(new Map()); // Clear cache
    fetchQueues(true); // Force refresh
  };

  const columns: TableColumn<QueueData>[] = [
    {
      title: t('queues.name'),
      field: 'queueName',
      sorting: false,
      render: (row) => (
        <span
          className={classes.linkStyle}
          onClick={() => {
            console.log('Queue clicked:', row.queueName);
            setSelectedQueue(row);
          }}
        >
          {row.queueName}
        </span>
      ),
      cellStyle: {
        fontWeight: theme.typography.fontWeightBold,
        maxWidth: '300px',
        wordBreak: 'break-all',
      },
    },
    {
      title: t('queues.currentConsumers'),
      field: 'currentConsumers',
      sorting: false,
      render: (row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Typography>
            {row.isLoading ? t('queues.loading') : row.error ? (
              <div>
                <span>{t('queues.failed')}</span>
                <Typography className={classes.errorText}>
                  {row.error}
                </Typography>
              </div>
            ) : (
              row.currentConsumers !== undefined
                ? formatNumber(row.currentConsumers)
                : t('queues.notAvailable')
            )}
          </Typography>
          {row.error && (
            <Tooltip title={t('queues.retry')} placement="top">
              <IconButton
                size="small"
                onClick={() => retryLoadingQueue(row)}
                disabled={retryingQueues.has(row.queueName)}
              >
                {retryingQueues.has(row.queueName) ? (
                  <CircularProgress size={16} />
                ) : (
                  <RetryIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: t('queues.currentMessages'),
      field: 'currentMessageCount',
      sorting: false,
      render: (row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Typography>
            {row.isLoading ? t('queues.loading') : row.error ? (
              <div>
                <span>{t('queues.failed')}</span>
                <Typography className={classes.errorText}>
                  {row.error}
                </Typography>
              </div>
            ) : (
              row.currentMessageCount !== undefined
                ? formatNumber(row.currentMessageCount)
                : t('queues.notAvailable')
            )}
          </Typography>
          {row.error && (
            <Tooltip title={t('queues.retry')} placement="top">
              <IconButton
                size="small"
                onClick={() => retryLoadingQueue(row)}
                disabled={retryingQueues.has(row.queueName)}
              >
                {retryingQueues.has(row.queueName) ? (
                  <CircularProgress size={16} />
                ) : (
                  <RetryIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: t('queues.bindSuccessCount'),
      field: 'bindSuccessCount',
      sorting: false,
      render: (row) => formatNumber(row.bindSuccessCount || 0),
    },
    {
      title: t('queues.accessType'),
      field: 'accessType',
      sorting: false,
    },
    {
      title: t('queues.bindRequestCount'),
      field: 'bindRequestCount',
      sorting: false,
      render: (row) => formatNumber(row.bindRequestCount || 0),
    },
  ];

  if (loading && queues.length === 0 && !searchQuery) {
    return <Progress />;
  }

  if (selectedQueue) {
    return (
      <QueueConsumersPage
        txFlowsUri={selectedQueue.txFlowsUri}
        queueName={selectedQueue.queueName}
        onBack={() => setSelectedQueue(null)}
      />
    );
  }

  return (
    <>
      <Header
        title={t('queues.title')}
        style={{ margin: 0 }}
      />
      <Content>
        <div className={`article ${classes.root}`}>
          <Table<QueueData>
            tableRef={tableRef}
            columns={columns}
            data={queues}
            options={{
              paging: true,
              pageSize: rowsPerPage,
              pageSizeOptions: [10, 20, 50, 100],
              emptyRowsWhenPaging: false,
              search: true,
              searchText: localSearchQuery,
              sorting: false,
              debounceInterval: 500,
              padding: 'dense',
              loadingType: 'linear',
              paginationType: "normal",
              actionsColumnIndex: -1,
              searchFieldStyle: { minWidth: 300 },
            }}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            onSearchChange={handleSearchChange}
            page={page}
            totalCount={totalCount}
            isLoading={loading}
            style={{ width: '100%' }}
            actions={[
              {
                icon: RefreshIcon,
                tooltip: t('queues.refresh'),
                isFreeAction: true,
                onClick: handleRefresh,
              },
            ]}
            icons={{
              ...Table.icons,
              Search: forwardRef((iconProps, ref) => (
                <SearchIcon {...iconProps} ref={ref} />
              )),
            }}
            localization={{
              pagination: tablePaginationLocalization(t),
              toolbar: {
                searchPlaceholder: t('queues.search'),
                searchTooltip: t('queues.searchTooltip')
              },
              header: {
                actions: ''
              },
              body: {
                emptyDataSourceMessage: (
                  <Typography color="textSecondary" className={classes.noDataMessage}>
                    {searchQuery
                      ? t('queues.emptySearch', { query: searchQuery })
                      : t('queues.empty')}
                  </Typography>
                )
              },
            }}
          />
        </div>
      </Content>
    </>
  );
};

export default QueueListPage;
